import React, {
  CSSProperties,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { MatchStatus } from "service";
import { getTeamColors } from "components/colors";
import TeamForm from "./components/TeamForm";
import {
  extractKickoff,
  parseFormFromSts,
  pick,
  toFormState,
  FormPoint,
  formatDate24,
  extractMatchDetails,
} from "./utils";
import { Shirt } from "components/common/Shirt";
import { Text } from "components/common/Text";
import MatchDetailsPanel from "./components/MatchDetailsPanel";

type PreMatchIntroProps = { status: MatchStatus };

interface KitInfo {
  id: number;
  si?: string | null;
  so?: string | null;
}

const getTeamBasics = (status: any, side: "t1" | "t2") => {
  const node = status?.[side] ?? (side === "t1" ? status?.T1 : status?.T2);
  const legacyName = side === "t1" ? status?.T1n : status?.T2n;
  const nestedName = node?.n;
  const name = nestedName || legacyName || "";

  const kitRaw = node?.kit || status?.c?.[side]?.kit || undefined;
  let kit: KitInfo | undefined;
  if (kitRaw && typeof kitRaw === "object") {
    const id = Number(kitRaw.id);
    const si = typeof kitRaw.si === "string" ? kitRaw.si : null;
    const so = typeof kitRaw.so === "string" ? kitRaw.so : null;
    if (Number.isFinite(id)) kit = { id, si, so };
  }

  return { name, kit } as {
    name: string;
    kit?: KitInfo;
  };
};

const normalizeForm = (arr: any[]): FormPoint[] => {
  if (!Array.isArray(arr)) return [];
  return arr.slice(-5).map((v) => ({ state: toFormState(v) }));
};

const PreMatchIntro: FC<PreMatchIntroProps> = ({ status }) => {
  const [activeTab, setActiveTab] = useState<"matchup" | "details">("matchup");
  const matchupPanelRef = useRef<HTMLDivElement | null>(null);
  const detailsPanelRef = useRef<HTMLDivElement | null>(null);
  const [contentHeight, setContentHeight] = useState(0);
  const [viewportHeightLimit, setViewportHeightLimit] = useState<number | null>(
    null,
  );
  const koDate = extractKickoff(status);
  const kickText = koDate ? formatDate24(koDate) : null;

  const infoText = (status as any)?.inf ?? null;

  const hb = getTeamBasics(status as any, "t1");
  const ab = getTeamBasics(status as any, "t2");
  const homeName = hb.name;
  const homeKit = hb.kit;
  const awayName = ab.name;
  const awayKit = ab.kit;

  const { home, away } = getTeamColors(status as any);

  const shirtPropsHome: any = homeKit ? { kit: homeKit } : { color: home };
  const shirtPropsAway: any = awayKit ? { kit: awayKit } : { color: away };

  const homeFormRaw = (pick(status, ["c.formH", "formH"]) as any[]) || [];
  const awayFormRaw = (pick(status, ["c.formA", "formA"]) as any[]) || [];

  let homeForm = normalizeForm(homeFormRaw);
  let awayForm = normalizeForm(awayFormRaw);

  if (homeForm.length === 0 && awayForm.length === 0) {
    const [hFromSts, aFromSts] = parseFormFromSts(status);
    if (hFromSts.length || aFromSts.length) {
      homeForm = hFromSts;
      awayForm = aFromSts;
    }
  }

  const teamNameStyle: CSSProperties = {
    minHeight: "calc(2 * 1.5em)",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    wordWrap: "break-word",
    overflowWrap: "anywhere",
  };

  const matchDetails = useMemo(() => extractMatchDetails(status), [status]);

  const tabs: { id: "matchup" | "details"; label: string }[] = [
    { id: "matchup", label: "Matchup" },
    { id: "details", label: "Match Details" },
  ];

  const updatePanelHeight = useCallback(() => {
    const matchupHeight = matchupPanelRef.current?.offsetHeight ?? 0;
    const detailsHeight = detailsPanelRef.current?.offsetHeight ?? 0;
    const nextHeight = Math.max(matchupHeight, detailsHeight);
    if (!nextHeight) return;
    setContentHeight((prev) => (prev === nextHeight ? prev : nextHeight));
  }, []);

  useEffect(() => {
    updatePanelHeight();

    if (typeof window === "undefined") return;

    const Observer = (
      window as typeof window & {
        ResizeObserver?: typeof ResizeObserver;
      }
    ).ResizeObserver;

    if (!Observer) {
      window.addEventListener("resize", updatePanelHeight);
      return () => window.removeEventListener("resize", updatePanelHeight);
    }

    const resizeObserver = new Observer(updatePanelHeight);
    const nodes = [matchupPanelRef.current, detailsPanelRef.current].filter(
      Boolean
    ) as HTMLDivElement[];

    nodes.forEach((node) => resizeObserver.observe(node));

    return () => resizeObserver.disconnect();
  }, [status, updatePanelHeight]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const updateLimit = () =>
      setViewportHeightLimit(Math.floor(window.innerHeight * 0.8));

    updateLimit();
    window.addEventListener("resize", updateLimit);

    return () => window.removeEventListener("resize", updateLimit);
  }, []);

  const renderTabButton = (id: "matchup" | "details", label: string) => {
    const isActive = activeTab === id;
    return (
      <button
        key={id}
        role="tab"
        aria-selected={isActive}
        className={`rounded-full px-2 py-1 font-semibold transition ${
          isActive
            ? "bg-white text-black shadow-lg"
            : "bg-white/10 text-white/70 hover:bg-white/20"
        }`}
        onClick={() => setActiveTab(id)}
      >
        <Text variant="prematchTabLabel">{label}</Text>
      </button>
    );
  };

  return (
    <div className="pointer-events-none absolute inset-0 z-20 grid place-items-center">
      <div
        role="dialog"
        aria-label="Pre-match information"
        className="pointer-events-auto w-[80%] max-h-[80%] overflow-y-auto rounded-lg border border-white/10 bg-[rgba(8,12,24,0.72)] px-[1vw] py-[4vw] shadow-[0_10px_40px_rgba(0,0,0,0.45)] backdrop-blur-md"
      >
        <div className="text-center text-white">
          {infoText ? (
            <div
              role="note"
              aria-label="League info"
              className="mb-[min(2.5vw,2.5vh)] flex flex-col items-center gap-1"
            >
              <div style={{ fontWeight: 700 }}>
                <Text variant="prematchInfoText">{infoText}</Text>
              </div>
            </div>
          ) : null}
          <div className="mb-[min(2.5vw,2.5vh)] flex flex-col items-center gap-1">
            <div className="text-white/80">
              <Text variant="prematchKickoffText">
                {kickText ? `Kick Off ${kickText}` : "Kick Off Soon"}
              </Text>
            </div>
          </div>
        </div>

        <div
          className="mb-[1.5vw] mt-[1vw] flex flex-wrap items-center justify-center gap-3"
          role="tablist"
          aria-label="Pre-match sections"
        >
          {tabs.map((tab) => renderTabButton(tab.id, tab.label))}
        </div>

        <div
          className="relative"
          style={
            contentHeight
              ? {
                  minHeight: Math.min(
                    contentHeight,
                    viewportHeightLimit ?? contentHeight,
                  ),
                }
              : undefined
          }
        >
          <div
            ref={matchupPanelRef}
            role="tabpanel"
            aria-hidden={activeTab !== "matchup"}
            className={`w-full ${
              activeTab === "matchup"
                ? "relative opacity-100"
                : "absolute inset-0 opacity-0"
            } transition-opacity duration-300 ${
              activeTab === "matchup"
                ? "pointer-events-auto"
                : "pointer-events-none"
            }`}
          >
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-[1vw]">
              <div className="grid place-items-center gap-2 text-center text-white">
                <Shirt {...shirtPropsHome} />
                <div
                  className="max-w-[80%] pt-[1vw] font-extrabold leading-tight text-white"
                  style={teamNameStyle}
                >
                  <Text variant="prematchTeamName">{homeName}</Text>
                </div>
                <TeamForm form={homeForm} />
              </div>

              <div className="text-center font-black text-white">
                <Text variant="prematchVsText">VS</Text>
              </div>

              <div className="grid place-items-center gap-2 text-center text-white">
                <Shirt {...shirtPropsAway} />
                <div
                  className="max-w-[80%] pt-[1vw] font-extrabold leading-tight text-white"
                  style={teamNameStyle}
                >
                  <Text variant="prematchTeamName">{awayName}</Text>
                </div>
                <TeamForm form={awayForm} />
              </div>
            </div>
          </div>

          <div
            ref={detailsPanelRef}
            role="tabpanel"
            aria-hidden={activeTab !== "details"}
            className={`w-full ${
              activeTab === "details"
                ? "relative opacity-100"
                : "absolute inset-0 opacity-0"
            } transition-opacity duration-300 ${
              activeTab === "details"
                ? "pointer-events-auto"
                : "pointer-events-none"
            }`}
          >
            <div className="flex h-full flex-col">
              <MatchDetailsPanel
                details={matchDetails}
                homeName={homeName}
                awayName={awayName}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreMatchIntro;
